import {
  Building2,
  DatabaseZap,
  FileText,
  Gauge,
  IndianRupee,
  Layers,
  Mail,
  MailCheck,
  MapPin,
  Monitor,
  Settings2,
  Telescope,
  ShieldCheck,
  User,
  Users,
  Wrench,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { useServerDevMode } from '@/hooks/useServerDevMode';
import { useAppSelector } from '@/store';
import { ROUTES, STORAGE_KEYS } from '@/utils/constants';

import { AdminSidebar, type AdminNavItem } from './AdminSidebar';
import { AdminTopBar } from './AdminTopBar';

const ADMIN_NAV: AdminNavItem[] = [
  {
    title: 'Overview',
    url: ROUTES.ADMIN_OVERVIEW,
    icon: Gauge,
    end: true,
  },
  { title: 'Approvals', url: ROUTES.APPROVALS, icon: ShieldCheck },
  { title: 'Users', url: ROUTES.ADMIN_USERS, icon: Users },
  { title: 'Sessions', url: ROUTES.ADMIN_SESSIONS, icon: Monitor },
  { title: 'Pincodes', url: ROUTES.ADMIN_PINCODES, icon: MapPin },
  { title: 'Custom Cities', url: ROUTES.ADMIN_CUSTOM_CITIES, icon: Building2 },
  { title: 'Email Templates', url: ROUTES.ADMIN_EMAIL_TEMPLATES, icon: Mail },
  { title: 'Email Logs', url: ROUTES.ADMIN_EMAIL_LOGS, icon: MailCheck },
  { title: 'T&C Documents', url: ROUTES.ADMIN_TC_DOCUMENTS, icon: FileText },
  { title: 'Company Settings', url: ROUTES.ADMIN_COMPANY_SETTINGS, icon: Settings2 },
  { title: 'Product Settings', url: ROUTES.ADMIN_PRICE_SETTINGS, icon: IndianRupee },
  { title: 'Slabs', url: ROUTES.ADMIN_SLABS, icon: Layers },
  { title: 'Data Migration', url: ROUTES.ADMIN_MIGRATION, icon: DatabaseZap },
  { title: 'Profile', url: ROUTES.ADMIN_PROFILE, icon: User },
  // ONE entry for all four MIS boards. They used to be four sidebar rows, which
  // made a quarter of the admin nav a single feature and buried everything
  // below it. They are still four independent pages on their own URLs; the
  // switch between them is the segmented control at the top of the MIS shell
  // (MisLayout), which is where a reader comparing two boards actually looks.
  // Telescope, not a chart glyph: the Analytics board inside the MIS shell
  // already owns the bar-chart icon, so reusing it here made the sidebar entry
  // look like a link to that one board rather than to all four. This row stands
  // for the whole management-information suite, so it gets a symbol none of the
  // four boards uses.
  { title: 'MIS', url: ROUTES.ADMIN_MIS, icon: Telescope },
];

/**
 * Dev Tools, shown only when the API is running in development.
 *
 * The page triggers destructive data resets. The SERVER is the real guard -
 * devReset.service refuses unless NODE_ENV is exactly 'development', so a
 * production deployment cannot truncate anything however the endpoint is
 * reached - but a live console must not advertise the button either. A visible
 * control that always errors is worse than no control.
 *
 * Which is why this is no longer `import.meta.env.DEV` alone: that is the
 * BUNDLE's mode, so `npm run dev` against a production API - how this project is
 * usually driven - still listed Dev Tools. useServerDevMode asks the API, and
 * keeps the build-time flag as its outer condition so a production bundle drops
 * the branch entirely rather than merely hiding it.
 */
const withDevTools = (nav: AdminNavItem[]): AdminNavItem[] => [
  ...nav.slice(0, nav.length - 2),
  { title: 'Dev Tools', url: ROUTES.ADMIN_DEV, icon: Wrench },
  ...nav.slice(nav.length - 2),
];

const STAFF_NAV: AdminNavItem[] = [
  { title: 'Email Templates', url: ROUTES.ADMIN_EMAIL_TEMPLATES, icon: Mail },
  { title: 'Email Logs', url: ROUTES.ADMIN_EMAIL_LOGS, icon: MailCheck },
  { title: 'T&C Documents', url: ROUTES.ADMIN_TC_DOCUMENTS, icon: FileText },
  { title: 'Profile', url: ROUTES.ADMIN_PROFILE, icon: User },
];

const AdminLayout = () => {
  const isAdmin = useAppSelector((state) => state.auth.user?.role === 'admin');
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(STORAGE_KEYS.ADMIN_NAV_COLLAPSED) === '1',
  );

  const showDevTools = useServerDevMode();
  const navItems = useMemo(() => {
    if (!isAdmin) return STAFF_NAV;
    return showDevTools ? withDevTools(ADMIN_NAV) : ADMIN_NAV;
  }, [isAdmin, showDevTools]);
  const homeUrl = isAdmin ? ROUTES.ADMIN_OVERVIEW : ROUTES.ADMIN_EMAIL_TEMPLATES;

  const handleToggle = (): void => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(STORAGE_KEYS.ADMIN_NAV_COLLAPSED, next ? '1' : '0');
      return next;
    });
  };

  return (
    /*
      `fixed inset-0`, deliberately, and not a height.

      This shell is the viewport and owns its own scroller (`main` below). As an
      in-flow block sized with `h-screen`/`h-[100dvh]` it still contributed that
      height to the DOCUMENT, so the document could end up taller than the
      viewport - and then the browser painted a SECOND scrollbar whose scroll
      slid the entire console upward and revealed the cream `body` background
      below the footer. Out of flow, the shell adds no document height and a
      fixed box cannot be scrolled away from the viewport, so neither symptom is
      reachable however the extra height arises.

      Nested positioning is unaffected: a fixed ancestor does not become the
      containing block for fixed descendants (only transform/filter/perspective/
      contain do), so the sidebar drawer and sticky table headers inside still
      resolve against the viewport.
    */
    <div className="fixed inset-0 flex overflow-hidden bg-background text-foreground">
      <AdminSidebar navItems={navItems} collapsed={collapsed} homeUrl={homeUrl} />

      <div className="flex min-w-0 flex-1 flex-col">
        <AdminTopBar onToggleSidebar={handleToggle} />

        {/* overscroll-contain: this is the app's scroll container, and without
            it the leftover scroll at either end is handed to the document,
            moving the entire shell. */}
        <main className="flex-1 overflow-auto overscroll-contain px-4 py-4">
          <Outlet />
        </main>

        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-card px-4 py-2 text-[11px] text-muted-foreground">
          <span>
            AdvisorKhoj MIS CRM · v0.1.0 ·{' '}
            <span className="font-semibold text-foreground">DEV</span> · © 2026 AdvisorKhoj
          </span>
          <a href="mailto:support@advisorkhoj.com" className="hover:text-foreground">
            support@advisorkhoj.com
          </a>
        </footer>
      </div>
    </div>
  );
};

export default AdminLayout;
